import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, MapPin, LogOut, ClipboardList } from "lucide-react";
import { Brand } from "../../components/Layout";
import { StatusBadge } from "../../components/StatusBadge";
import { useAuth } from "../../context/AuthContext";
import { api, euro } from "../../lib/api";
import { Button } from "../../components/ui/button";

export default function PartnerDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState(null);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    api.get("/partner/orders").then((r) => setOrders(r.data)).catch(() => setOrders([]));
    api.get("/partner/profile").then((r) => setProfile(r.data)).catch(() => {});
  }, []);

  const active = (orders || []).filter((o) => o.status !== "completed");
  const done = (orders || []).filter((o) => o.status === "completed");

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-[#0F172A] text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Brand light />
            <span className="hidden sm:inline text-xs font-semibold bg-brand-orange px-2 py-1 rounded-full">PARTNER</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-sm text-slate-300">{profile?.company_name || user?.name}</span>
            <Button variant="outline" size="sm" className="bg-transparent border-white/25 text-white hover:bg-white/10 hover:text-white" onClick={() => { logout(); navigate("/"); }} data-testid="partner-logout">
              <LogOut size={16} className="mr-1" /> Uitloggen
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <h1 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-2"><ClipboardList /> Mijn opdrachten</h1>

        {orders === null ? (
          <div className="flex justify-center py-20"><Loader2 className="animate-spin text-slate-400" /></div>
        ) : (
          <>
            <h2 className="mt-8 mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500">Actieve opdrachten ({active.length})</h2>
            {active.length === 0 ? <p className="text-slate-500 text-sm">Geen actieve opdrachten.</p> : (
              <div className="grid gap-4 sm:grid-cols-2">
                {active.map((o) => <OrderCard key={o.id} o={o} onClick={() => navigate(`/partner/opdracht/${o.id}`)} />)}
              </div>
            )}
            {done.length > 0 && (
              <>
                <h2 className="mt-10 mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500">Afgerond ({done.length})</h2>
                <div className="grid gap-4 sm:grid-cols-2">
                  {done.map((o) => <OrderCard key={o.id} o={o} onClick={() => navigate(`/partner/opdracht/${o.id}`)} />)}
                </div>
              </>
            )}
          </>
        )}
      </main>
    </div>
  );
}

function OrderCard({ o, onClick }) {
  return (
    <button onClick={onClick} data-testid={`partner-order-${o.booking_number}`} className="text-left bg-white border border-slate-200 rounded-xl p-5 hover:shadow-md hover:border-orange-300 transition-all">
      <div className="flex items-center justify-between">
        <span className="font-heading font-bold text-slate-900">{o.booking_number}</span>
        <StatusBadge status={o.status} />
      </div>
      <p className="mt-2 text-sm font-medium text-slate-800">{o.service_name} · {o.quantity}x {o.item_type}</p>
      <p className="mt-1 text-sm text-slate-500 flex items-center gap-1"><MapPin size={14} /> {o.location.postal_code} {o.location.city}</p>
      <div className="mt-3 flex items-center justify-between">
        <span className="text-xs text-slate-400">Vergoeding</span>
        <span className="font-bold text-green-700">{euro(o.partner_fee)}</span>
      </div>
    </button>
  );
}
